﻿// © Microsoft Corporation. All rights reserved.

/**
 * The aria-label html attribute
 * @type {string}
 */
export const ariaLabelAttribute = "aria-label";

/**
 * The id attribute on the <div> element that is the root element for Peregrine Pages / Experiences.
 * There is only ever 1 root element on a page
 * @type {string}
 */
export const rootElementId = "msnewsroot";

/**
 * Element names that are commonly used in Peregrine pages and experiences
 * @type {object}
 */
export const htmlElementIds: any = {
    html: "html",
    head: "head",
    body: "body"
};

/**
 * Constants used by application cache related scenarios
 */
export const applicationCacheConstants = {
    /**
     * Attribute name on html tag that is set when appcache is being used on the page
     */
    manifestAttributeName: "manifest",

    /**
     * Local storage key that holds the appcache info used to determine page refresh needs
     * for different page types.
     */
    pagesRefreshData: "ac_prd"
};

/**
 * The attributes that are part of markup elements that we care about
 */
export const clientSettings = "data-client-settings";
export const pagetypeSettings = "data-pagetype";
export const id = "data-id";
export const info = "data-info";
export const js = "data-js";
export const t = "data-t";
export const lang = "lang";
export const dir = "dir";
export const serviceUrl = "data-service-url";
export const telemetryId = "data-telemetry-id";
export const trackPageProduct = "data-page-product";
export const trackPageType = "data-page-type";

/**
 * The number of characters that our Service Request Ids must have. This is currently defined by OneService
 * @type {number}
 */
export const numberOfCharactersNeededForUniqueRequestId = 32;

/**
 * The minimum number of path segments that will be in the URL which hosts MS News Experience pages.
 * The first path segment should be the vertical key, e.g. spartan and the second path segment
 * should be the the category key, e.g. ntp
 * @type {number}
 */
export const minimumHostPagePathSegments = 2;

/**
 * Default value we use for the name of the cookie that holds the ANID
 */
export const anonCookieName = "APP_ANON";

/**
 * App Anon Cookie Exists Cookie Name. This cookie is a marker if APP_ANON exist or not.
 * This is necessary because APP_ANON is HTTP cookie and not accessible by JS.
 */
export const appAnonCookieExistsCookieName = "aace";

/**
 * Child cookie name, used to signal if rpsUser is child account.
 */
export const childCookieName = "child";

/**
 * Tracks user engagement for the Meet Now feature via the first day it was shown
 */
export const meetNowEngagementDayFirstShown = "meetnow-day-first-shown";

/**
 * Tracks user engagment for the Meet Now feature via the number of clicks
 */
export const meetNowEngagementNumberOfClicks = "meetnow-number-clicks";

/**
 * Tracks whether user engagement requirements for the Meet Now feature have been met
 */
export const meetNowengagementReached = "meetnow-engagement-reached";

/**
 * Migration consent state cookie, indicates user has already granted or denied migration consent
 */
export const migrationConsentStateKey = "migration-state";

/**
 * Id of MSCC cookie banner container element.
 */
export const msccCookieBannerElementId = "consent-banner-container";

/**
 * Nurturing accepted state key is intended to be used in local storage, indicates user has opted in or out of Iris targeted content.
 */
export const nurturingAcceptedStateKey = "nurturingAccepted";

/**
 * Recommended search key is intended to be used in local storage,
 * indicates user has opted in or out to show recommended search below search box
 */
export const recommendedSearchKey = "recommended-search-enabled";

/**
 * Search history key is intended to be used in local storage, indicates user has opted in or out to show search history
 */
export const searchHistoryKey = "search-history-enabled";

/**
 * Local storage key to indicated if no search history is expected based on auth logic
 * If it is expected then do not show search history toggle, otherwise show search history toggle in settings dialog
 */
export const noSearchHistoryExpectedKey = "no-search-history-expected-auth";

/**
 * key which is used to force consumer mode for Enterprise Users.
 */
export const EnterpriseToConsumerOverrideKey = "forceConsumer";

/**
 * Headers used for one service requests
 */
export const oneServiceHeaders = {
    activityId: "x-client-activityid",
    authorization: "Authorization",
    entAuthorization: "Ent-Authorization"
};

/**
 * used query string params in OneService requests
 */
export const oneServiceQueryStringKeys = {
    activityId: "activityId",
    apiKey: "apikey",
    complexInfoPaneEnabled: "cipenabled",
    contentType: "contentType",
    delta: "delta",
    disableTypeSerialization: "DisableTypeSerialization",
    infopaneCount: "infopaneCount",
    fdhead: "fdhead",
    feedLayoutRequestType: "t",
    filter: "$filter",
    ids: "ids",
    location: "location",
    market: "market",
    ocid: "ocid",
    parentContentId: "parent-content-id",
    parentNamespace: "parent-ns",
    parentTitle: "parent-title",
    queryQ: "q",
    query: "query",
    queryType: "queryType",
    qScope: "qscope",
    responseSchema: "responseSchema", // IDX returns nextPageUrl with camel cased responseSchema query string
    select: "$select",
    session: "session",
    signInCookieName: "scn",
    skip: "$skip",
    source: "source",
    timeOut: "timeOut",
    top: "$top",
    type: "type",
    user: "user",
    wrapOData: "wrapodata",
    infoPaneItemCount: "t:InfoPane.itemCount",
    newsTop: "newsTop",
    newsSkip: "newsSkip"
};

/**
 * Constants to use as role attribute value for elements
 */
export enum AriaRoles {
    /**
     * Presentation role is used to remove semantic meaning from an element and any of its related child elements.
     */
    Presentation = "presentation"
}

/**
 * Basic enum values for mobile device orientation
 */
export enum DeviceOrientation {
    Unknown = "Unknown",
    Portrait = "Portrait",
    Landscape = "Landscape"
}

/**
 * Common local storage keys
 */
export const CoreLocalStorageKeys = {
    /**
     * This key is used to store a dictionary of app versions per app type based on the current page version from the DOM.
     * Note that the version value will represent the current **loaded page** version and **not the new version** in scenarios
     * where the page is cached and require a new version to be loaded upon version detection (i.e., appcache, or service worker).
     * In these cached/refresh scenarios, we still want to store the version that the page was actually loaded from.
    */
    pageVersions: "pageVersions"
};

/**
 * The default APIKey for OneService requests
 */
export const defaultOneServiceApiKey = "0QfOX3Vn51YCzitbLaRkTTBadtWpgTN8NZLW0C1SEM";

/**
 * Url for microsoft news page
 */
export const MicrosoftNewsUrl = "https://microsoftnews.msn.com/";

/**
 * Local storage key for user's currently selected pivot (used for enterprise)
 */
export const SelectedPivotStorageId = "selectedPivot";
